// GPSCheckInModal.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BiLoaderAlt, BiMapPin, BiCheckCircle, BiBullseye } from 'react-icons/bi';
import { API_BASE_URL } from '../utils/urls.js';

const GPSCheckInModal = ({ userId, onClose, onCheckInSuccess }) => {
    const [status, setStatus] = useState('getting_location'); // getting_location, ready, submitting, success, error
    const [coordinates, setCoordinates] = useState({ latitude: null, longitude: null });
    const [locationName, setLocationName] = useState('Fetching address...');


    const CHECKIN_TYPES = ['Shift_Start', 'Shift_End', 'Break_Start', 'Break_End'];

    // --- Geolocation and Reverse Geocoding ---
    useEffect(() => {
        const fetchLocation = () => {
            if (!navigator.geolocation) {
                setLocationName('Geolocation is not supported by your browser.');
                setStatus('error');
                return;
            }

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude, accuracy } = position.coords;

                    // Log accuracy for debugging (accuracy is in meters)
                    console.log(`GPS Accuracy: ${accuracy}m`);

                    // OPTIONAL: If accuracy is worse than 30 meters, you might want to 
                    // warn the user or try again, as this is usually why addresses are off.
                    if (accuracy > 30) {
                        console.warn("Low accuracy detected. Address might be slightly off.");
                    }

                    setCoordinates({ latitude, longitude });
                    reverseGeocode(latitude, longitude);
                },
                (err) => {
                    console.error('Geolocation Error:', err);

                    // Handle specific timeout error
                    if (err.code === 3) {
                        setLocationName('GPS timeout. Please try again in an open area.');
                    } else {
                        setLocationName('Unable to get GPS location.');
                    }
                    setStatus('error');
                },
                {
                    enableHighAccuracy: true,
                    timeout: 15000,    // Increased to 15s to give the hardware time to lock
                    maximumAge: 0      // Ensure we aren't using a cached location from the last house
                }
            );
        };

        const reverseGeocode = async (lat, lon) => {
            try {
                // Call the new geocoding utility API
                const token = localStorage.getItem('token');
                const res = await axios.get(`${API_BASE_URL}/cases/utils/geocode/reverse?latitude=${lat}&longitude=${lon}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (res.data.location_name) {
                    setLocationName(res.data.location_name);
                    setStatus('ready');
                } else {
                    setLocationName('Address not found for these coordinates.');
                    setStatus('ready');
                }
            } catch (error) {
                console.error('Reverse Geocoding API Error:', error);
                setLocationName('Geocoding service unavailable.');
                setStatus('ready');
            }
        };

        fetchLocation();
    }, []);

    // --- Submission Handler ---
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (status !== 'ready' || !coordinates.latitude || !coordinates.longitude) return;

        setStatus('submitting');
        const token = localStorage.getItem('token');

        try {
            // API endpoint based on saved user info: /users/${userId}/checkin
            const res = await axios.post(`${API_BASE_URL}/users/${userId}/checkin`, {
                latitude: coordinates.latitude,
                longitude: coordinates.longitude,
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setStatus('success');
            // Pass the details needed to update the dashboard's last check-in display
            const submissionDetails = {
                ...res.data, // Contains locationId, message
                location_name: locationName,
                created_at: new Date().toISOString(),
            };
            onCheckInSuccess(submissionDetails);

        } catch (error) {
            console.error('Check-in Submission Error:', error);
            setStatus('error');
            setLocationName(`Check-in failed: ${error.response?.data?.message || 'Server error'}`);
            // Keep the modal open on error to show the message
            setTimeout(onClose, 3000);
        }
    };

    // --- Render Logic (Tailwind CSS for UI) ---
    const isReady = status === 'ready';

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-50 flex justify-center items-center">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                    <BiBullseye className="w-6 h-6 mr-2 text-indigo-600" /> GPS Check-In
                </h3>

                <form onSubmit={handleSubmit}>
                    <div className="mb-4 p-3 bg-gray-50 rounded-md border border-gray-200">
                        <label className="block text-sm font-medium text-gray-700">Current GPS Location:</label>
                        <div className={`mt-1 flex items-center ${isReady ? 'text-indigo-600' : 'text-gray-500'}`}>
                            {status === 'getting_location' && <BiLoaderAlt className="w-4 h-4 animate-spin mr-2" />}
                            <BiMapPin className="w-4 h-4 mr-2" />
                            <span className="text-sm font-medium">{locationName}</span>
                        </div>
                        {coordinates.latitude && (
                            <p className="text-xs text-gray-500 mt-1">Lat: {coordinates.latitude.toFixed(6)}, Lon: {coordinates.longitude.toFixed(6)}</p>
                        )}
                    </div>



                    <div className="flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
                            disabled={status === 'submitting'}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={`px-4 py-2 text-sm font-medium text-white rounded-md shadow-sm transition duration-150 ease-in-out flex items-center space-x-1 ${isReady ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-400 cursor-not-allowed'
                                }`}
                            disabled={!isReady}
                        >
                            {status === 'submitting' && <BiLoaderAlt className="w-4 h-4 animate-spin" />}
                            {status === 'success' && <BiCheckCircle className="w-4 h-4" />}
                            <span>{status === 'submitting' ? 'Checking In...' : status === 'success' ? 'Recorded!' : 'Confirm Check-In'}</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default GPSCheckInModal;